import { Link, useNavigate, useLocation } from "react-router-dom";
import { LogOut, LayoutDashboard, Users, Building2, BookOpenCheck, Workflow, Gauge, ScrollText, Briefcase } from "lucide-react";
import { Logo } from "@/components/marketing/Nav";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/admin", label: "Overview", Icon: LayoutDashboard, exact: true },
  { to: "/admin/users", label: "Users", Icon: Users },
  { to: "/admin/organizations", label: "Organizations", Icon: Building2 },
  { to: "/admin/knowledge", label: "Knowledge", Icon: BookOpenCheck },
  { to: "/admin/cases", label: "Cases", Icon: Briefcase },
  { to: "/admin/automations", label: "Automations", Icon: Workflow },
  { to: "/admin/performance", label: "Performance", Icon: Gauge },
  { to: "/admin/audit", label: "Audit log", Icon: ScrollText },
];

export default function AdminShell({ children, title, subtitle, actions }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const signOut = () => { logout(); navigate("/"); };
  const isActive = (n) => (n.exact ? pathname === n.to : pathname.startsWith(n.to));

  return (
    <div className="min-h-screen bg-softcream lg:flex" data-testid="admin-shell">
      <aside className="hidden w-64 shrink-0 bg-deepnavy lg:flex lg:flex-col">
        <div className="flex items-center gap-2 border-b border-white/10 p-5">
          <Logo dark />
          <span className="rounded bg-brandyellow/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brandyellow">Admin</span>
        </div>
        <nav className="flex-1 space-y-1 p-3" aria-label="Admin">
          {NAV.map((n) => n.soon ? (
            <div key={n.label} className="flex items-center justify-between rounded-lg px-3 py-2.5 text-sm text-white/35" data-testid={`admin-nav-${n.label.toLowerCase().replace(" ", "-")}-soon`}>
              <span className="flex items-center gap-3"><n.Icon size={17} /> {n.label}</span>
              <span className="rounded bg-white/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wide">Soon</span>
            </div>
          ) : (
            <Link key={n.to} to={n.to} data-testid={`admin-nav-${n.label.toLowerCase().replace(" ", "-")}`}
              className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive(n) ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5 hover:text-white"}`}>
              <n.Icon size={17} /> {n.label}
            </Link>
          ))}
        </nav>
        <div className="border-t border-white/10 p-3">
          <p className="px-3 text-sm text-white/80">{user?.name}</p>
          <p className="px-3 text-xs text-white/40">{user?.role?.replace("_", " ")}</p>
          <button onClick={signOut} className="mt-2 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-white/70 hover:bg-white/5" data-testid="admin-logout">
            <LogOut size={17} /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex items-center justify-between bg-deepnavy px-4 py-3 lg:hidden">
        <Logo dark />
        <button onClick={signOut} className="text-white/80" aria-label="Sign out" data-testid="admin-logout-mobile"><LogOut size={18} /></button>
      </div>

      <div className="min-w-0 flex-1">
        <div className="border-b border-hborder bg-white px-4 py-5 sm:px-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="font-serif text-2xl tracking-tight text-heading" data-testid="admin-page-title">{title}</h1>
              {subtitle && <p className="mt-0.5 text-sm text-muted">{subtitle}</p>}
            </div>
            {actions}
          </div>
        </div>
        <div className="p-4 pb-24 sm:p-8 lg:pb-8">{children}</div>
        <nav className="fixed inset-x-0 bottom-0 flex overflow-x-auto border-t border-hborder bg-white lg:hidden" aria-label="Admin mobile">
          {NAV.filter((n) => !n.soon).map((n) => (
            <Link key={n.to} to={n.to} className={`flex min-w-[76px] flex-1 flex-col items-center gap-0.5 py-2.5 text-[11px] ${isActive(n) ? "text-tblue" : "text-muted"}`}>
              <n.Icon size={18} />{n.label}
            </Link>
          ))}
        </nav>
      </div>
    </div>
  );
}
